import { instantOf } from '../freshness.js'
import { STALE_AFTER_MS, periodName, statusText, surname } from './model.js'

// What the game view says about one game's own object. P6-328b (GN-1b).
//
// Pure functions over `3a/hoops/games/<id>.json` (the producer's `_game_blob`): the box
// score, the plays and the four tiles. Nothing here reads a clock or a zone.
//
// ═══════════════════════════════════════════════════════════════════════════
// 🔴 A PLAYER WITH NO LINE HAS NO LINE: `–`, NEVER `0`
// ═══════════════════════════════════════════════════════════════════════════
//
// `utils/hoops_sources.py::_player` gives a player who has not played `null` for his minutes,
// every statistic and his plus-minus — *"a bench player who has not entered yet is 'no line',
// not '0 points'"* — and `P6-156`'s rule is the reason: a count is a claim and an absence is not.
// Every formatter below therefore returns `null` for a missing figure and the caller draws a
// dash; none of them coerces with `?? 0`, which would turn "has not played" into "scored
// nothing" on a screen people read at halftime.

/** `34:27` to `34`: whole minutes, as the design prints them in a 24px column.
 *
 * Rounded, and at least `1` for a player who has been on the floor at all; `null` for one who
 * has not (the design's `played = sec > 0`). */
export function wholeMinutes(minutes) {
  const match = typeof minutes === 'string' ? /^(\d+):(\d{2})$/.exec(minutes) : null
  if (match === null) return null
  const seconds = Number(match[1]) * 60 + Number(match[2])
  return seconds <= 0 ? null : Math.max(1, Math.round(seconds / 60))
}

const figure = (value) => (Number.isInteger(value) ? String(value) : null)
const pair = (made, attempted) =>
  Number.isInteger(made) && Number.isInteger(attempted) ? `${made}-${attempted}` : null

/** `+5`, `-3`, `0`; `null` when there is no line. */
export function plusMinus(value) {
  if (!Number.isInteger(value)) return null
  return value > 0 ? `+${value}` : String(value)
}

/** The four columns a phone shows: MIN, PTS, REB, AST. `null` is a dash. */
export function phoneCells(player) {
  return [wholeMinutes(player.minutes), figure(player.points), figure(player.rebounds), figure(player.assists)]
}

/** Every column, from 720px: MIN, FG, 3PT, FT, REB, AST, STL, BLK, TO, PF, +/-, PTS. */
export function fullCells(player) {
  return [
    wholeMinutes(player.minutes),
    pair(player.fg_made, player.fg_attempted),
    pair(player.three_made, player.three_attempted),
    pair(player.ft_made, player.ft_attempted),
    figure(player.rebounds),
    figure(player.assists),
    figure(player.steals),
    figure(player.blocks),
    figure(player.turnovers),
    figure(player.fouls),
    plusMinus(player.plus_minus),
    figure(player.points),
  ]
}

/**
 * The full line a tap opens on a phone: `#9 · SF · FG 1-2 · 3PT 0-1 · FT 4-4 · STL 0 · BLK 1 ·
 * TO 0 · PF 0 · +/- -4`. Only what is there: a figure that is missing is left out.
 */
export function detailText(player) {
  const parts = [
    typeof player.jersey === 'string' && player.jersey !== '' ? `#${player.jersey}` : null,
    typeof player.position === 'string' && player.position !== '' ? player.position : null,
    pair(player.fg_made, player.fg_attempted) && `FG ${pair(player.fg_made, player.fg_attempted)}`,
    pair(player.three_made, player.three_attempted) &&
      `3PT ${pair(player.three_made, player.three_attempted)}`,
    pair(player.ft_made, player.ft_attempted) && `FT ${pair(player.ft_made, player.ft_attempted)}`,
    figure(player.steals) !== null ? `STL ${player.steals}` : null,
    figure(player.blocks) !== null ? `BLK ${player.blocks}` : null,
    figure(player.turnovers) !== null ? `TO ${player.turnovers}` : null,
    figure(player.fouls) !== null ? `PF ${player.fouls}` : null,
    plusMinus(player.plus_minus) !== null ? `+/- ${plusMinus(player.plus_minus)}` : null,
  ]
  return parts.filter(Boolean).join(' · ')
}

/** `J. Brunson`, or just `Brunson` where there is no room for the initial. */
export function shortName(name, surnameOnly = false) {
  if (typeof name !== 'string' || name.trim() === '') return ''
  const parts = name.trim().split(/\s+/)
  if (parts.length === 1) return parts[0]
  return surnameOnly ? surname(name) : `${parts[0].charAt(0)}. ${surname(name)}`
}

/** Two letters for a disc standing in for a headshot: `JB`, `KAT`→`KT`. */
export function initials(name) {
  if (typeof name !== 'string') return ''
  return name
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .map((word) => word.charAt(0))
    .join('')
    .slice(0, 2)
    .toUpperCase()
}

/**
 * The feed's reason code, as a sentence fragment: `INACTIVE_INJURY` is `Injury`,
 * `INACTIVE_GLEAGUE_TWOWAY` is `G League two-way`, `DND_INJURY` is `Injury`.
 *
 * ⚠️ WHAT IS KEPT AND WHAT IS NOT. The status prefix (`INACTIVE`, `DND`, `NWT`) is dropped:
 * it is the feed's filing, and the page's heading already says these players are not
 * playing. The rest is words. An unrecognised code is still words — lower-cased, spaces for
 * underscores — rather than a raw constant on a screen; the code itself is the row's `title`.
 */
export function dnpReason(code) {
  if (typeof code !== 'string' || code.trim() === '') return null
  const words = code
    .trim()
    .split('_')
    .filter(Boolean)
    .map((word) => word.toLowerCase())
  if (words.length > 1 && ['inactive', 'dnd', 'nwt'].includes(words[0])) words.shift()
  const text = words
    .join(' ')
    .replace(/\bgleague\b/, 'G League')
    .replace(/\btwoway\b/, 'two-way')
  return text.charAt(0).toUpperCase() + text.slice(1)
}

/** A player the feed lists as not playing, with a reason: the brief's definition of a DNP. */
export function isNotPlaying(player) {
  return player.played !== true && typeof player.not_playing_reason === 'string' && player.not_playing_reason !== ''
}

/**
 * One team's roster, split as the box score is drawn: starters, bench, and the players who are
 * not playing. Feed order within each group. A player who has not played and has no reason
 * (a bench player who has not entered) stays on the bench with dashes: he IS available.
 */
export function rosterGroups(players) {
  const rows = Array.isArray(players) ? players : []
  return {
    starters: rows.filter((player) => player.starter === true && !isNotPlaying(player)),
    bench: rows.filter((player) => player.starter !== true && !isNotPlaying(player)),
    notPlaying: rows.filter(isNotPlaying),
  }
}

/** A team's total line, in the same column order as `phoneCells` / `fullCells`.
 *
 * The MIN and +/- cells are `''`, which is BLANK, where a missing figure is `null` and drawn as a
 * dash: a team has no minutes or plus-minus of its own, and a dash there would say a figure is
 * missing when there is none to have. */
export function totalsCells(totals, phone, fantasy = false) {
  // The Fpts column's total is left empty (P6-333a): a team's fantasy total is the viewer's own and lives in the panel.
  const extra = fantasy ? [''] : []
  if (phone) return ['', figure(totals.points), figure(totals.rebounds), figure(totals.assists), ...extra]
  return [
    '',
    pair(totals.fg_made, totals.fg_attempted),
    pair(totals.three_made, totals.three_attempted),
    pair(totals.ft_made, totals.ft_attempted),
    figure(totals.rebounds),
    figure(totals.assists),
    figure(totals.steals),
    figure(totals.blocks),
    figure(totals.turnovers),
    figure(totals.fouls),
    '',
    figure(totals.points),
    ...extra,
  ]
}

const percent = (made, attempted) =>
  Number.isInteger(made) && Number.isInteger(attempted) && attempted > 0 ? (100 * made) / attempted : null

/** `FG 45.2% · 6 TO left`, the note at the right of a team's box-score heading. */
export function teamNote(totals, side) {
  const fg = percent(totals?.fg_made, totals?.fg_attempted)
  return [
    fg === null ? null : `FG ${fg.toFixed(1)}%`,
    Number.isInteger(side?.timeouts) ? `${side.timeouts} TO left` : null,
  ]
    .filter(Boolean)
    .join(' · ')
}

/**
 * The columns of the score-by-period table: `1 2 3 4`, then `OT`, `2OT`…, each with the points a
 * side scored in it, `null` for a period not played yet, and a total.
 *
 * Always at least four columns, so a game in the second quarter shows the third and fourth as
 * dashes rather than not existing. Overtime columns appear when a period past the fourth has been
 * played. ⚠️ The producer publishes points by period PLAYED SO FAR, so "no entry" is "not played",
 * and a quarter in which a side scored nothing is a `0` — which is a fact, and is drawn as one.
 */
export function periodTable(away, home) {
  const awayPeriods = Array.isArray(away?.periods) ? away.periods : []
  const homePeriods = Array.isArray(home?.periods) ? home.periods : []
  const count = Math.max(4, awayPeriods.length, homePeriods.length)
  const labels = Array.from({ length: count }, (_, i) => (i < 4 ? String(i + 1) : periodName(i + 1)))
  const cell = (list, i) => (Number.isInteger(list[i]) ? list[i] : null)
  return {
    labels,
    away: Array.from({ length: count }, (_, i) => cell(awayPeriods, i)),
    home: Array.from({ length: count }, (_, i) => cell(homePeriods, i)),
  }
}

/** The four tiles: lead changes, times tied, biggest lead, biggest run. */
export function tiles(game) {
  const away = game.away?.tricode ?? 'Away'
  const home = game.home?.tricode ?? 'Home'
  const count = (value) => (Number.isInteger(value) ? String(value) : '–')

  const sideOf = (aValue, hValue, format) => {
    const a = Number.isInteger(aValue) ? aValue : null
    const h = Number.isInteger(hValue) ? hValue : null
    const best = Math.max(a ?? 0, h ?? 0)
    // Nobody has led, or the feed said nothing: a dash, not a team with a zero.
    if (best === 0) return { value: '–', sub: null }
    const leader = a === h ? 'Both' : (a ?? 0) > (h ?? 0) ? away : home
    return {
      value: `${leader} ${format(best)}`,
      sub: `${away} ${a === null ? '–' : format(a)} · ${home} ${h === null ? '–' : format(h)}`,
    }
  }

  const lead = sideOf(game.totals?.away?.biggest_lead, game.totals?.home?.biggest_lead, String)
  const run = sideOf(
    game.totals?.away?.biggest_run,
    game.totals?.home?.biggest_run,
    (value) => `${value}–0`,
  )
  return [
    { key: 'lead-changes', label: 'Lead changes', value: count(game.lead_changes), sub: null },
    { key: 'times-tied', label: 'Times tied', value: count(game.times_tied), sub: null },
    { key: 'biggest-lead', label: 'Biggest lead', ...lead },
    { key: 'biggest-run', label: 'Biggest run', ...run },
  ]
}

/** The two heads-up figures the wide hero draws per team: the top scorer, rebounder and passer
 * among players who have a line, first listed on a tie. */
export function teamLeaders(players) {
  const played = (Array.isArray(players) ? players : []).filter((player) => player.played === true)
  return [
    ['points', 'PTS'],
    ['rebounds', 'REB'],
    ['assists', 'AST'],
  ]
    .map(([key, unit]) => {
      let best = null
      for (const player of played) {
        if (!Number.isInteger(player[key])) continue
        if (best === null || player[key] > best[key]) best = player
      }
      return best === null ? null : { key, unit, player: best, value: best[key] }
    })
    .filter(Boolean)
}

/**
 * The team-stat bars on the wide hero, from `totals`: FG%, 3PT%, FT%, rebounds, assists, steals
 * and turnovers (the one where less is better).
 *
 * `aShare` is the away side's share of the bar as a percentage, 50 when there is nothing to
 * compare. A figure the totals do not have is `null` and drawn as a dash, never as `0`.
 */
export function compareRows(totalsAway, totalsHome) {
  const rows = [
    ['FG%', percent(totalsAway?.fg_made, totalsAway?.fg_attempted), percent(totalsHome?.fg_made, totalsHome?.fg_attempted), true, false],
    ['3PT%', percent(totalsAway?.three_made, totalsAway?.three_attempted), percent(totalsHome?.three_made, totalsHome?.three_attempted), true, false],
    ['FT%', percent(totalsAway?.ft_made, totalsAway?.ft_attempted), percent(totalsHome?.ft_made, totalsHome?.ft_attempted), true, false],
    ['Rebounds', intOrNull(totalsAway?.rebounds), intOrNull(totalsHome?.rebounds), false, false],
    ['Assists', intOrNull(totalsAway?.assists), intOrNull(totalsHome?.assists), false, false],
    ['Steals', intOrNull(totalsAway?.steals), intOrNull(totalsHome?.steals), false, false],
    ['Turnovers', intOrNull(totalsAway?.turnovers), intOrNull(totalsHome?.turnovers), false, true],
  ]
  return rows.map(([label, a, h, isPercent, lowerIsBetter]) => {
    const known = a !== null && h !== null
    const total = known ? a + h : 0
    const aShare = known && total > 0 ? (a / total) * 100 : 50
    const aBetter = known && a !== h ? (lowerIsBetter ? a < h : a > h) : false
    const hBetter = known && a !== h ? !aBetter : false
    const text = (value) => (value === null ? '–' : isPercent ? value.toFixed(1) : String(value))
    return { label, a: text(a), h: text(h), aShare, aBetter, hBetter }
  })
}

function intOrNull(value) {
  return Number.isInteger(value) ? value : null
}

const ADVANCE = Object.freeze({ pregame: 0, live: 1, final: 2 })

/**
 * Everything the game view's hero needs, from the game's own object and, when the board has
 * it, the card for the same game.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * 🔴 TWO OBJECTS, ONE GAME, AND THE ONE THAT IS THE BOX SCORE WINS WHERE IT CAN
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * The scores, the periods, the timeouts and the bonus are the game's own object's (it IS the box
 * score; the card is a mixture of it and the scoreboard, and the producer's invariant is that a
 * card never shows an older score than its game view). The game object does NOT carry the period or
 * the clock — those are card fields — so the status line reads them from the card, and from nowhere
 * else.
 *
 * ⚠️ THE STATUS NEVER GOES BACKWARDS, the bot's own rule, applied to the page's two reads of one
 * game: they are separate requests and the card can be a poll ahead of the object or behind it.
 * The more advanced of the two is the status, and the card's period and clock are used only when the
 * card is as far along as that status (a card that still says "live" under a game that is final has
 * a clock that is no longer true, so the final's own period count is used instead).
 *
 * ⚠️ WITH NO CARD (the board has rolled over, or has not been read yet) THE LINE SAYS ONLY WHAT THE
 * OBJECT SAYS: `Live`, or `Final` and `Final · OT` from the number of periods played. It does not
 * guess a quarter.
 */
export function heroModel({ game, card, nowMs }) {
  const away = game?.away ?? card?.away ?? null
  const home = game?.home ?? card?.home ?? null
  if (away === null || home === null) return null
  const known = [game?.status, card?.status].filter((status) => Object.hasOwn(ADVANCE, status ?? ''))
  if (known.length === 0) return null
  const status = known.reduce((best, next) => (ADVANCE[next] > ADVANCE[best] ? next : best))

  const stampMs = instantOf(game ? (game.fetched_at ?? game.as_of) : card?.as_of)
  const stale = status === 'live' && stampMs !== null && nowMs - stampMs >= STALE_AFTER_MS
  const kind = stale ? 'stale' : status

  const cardIsCurrent = Boolean(card) && card.status === status
  const played = Math.max(
    Array.isArray(away.periods) ? away.periods.length : 0,
    Array.isArray(home.periods) ? home.periods.length : 0,
  )
  const lineCard = {
    status,
    period: cardIsCurrent ? card.period : status === 'final' && played > 0 ? played : null,
    clock: cardIsCurrent ? card.clock : undefined,
    tip_utc: card?.tip_utc,
  }
  return {
    kind,
    status,
    stampMs,
    source: game ? game.source : card?.source,
    statusLine: statusText(lineCard, kind, nowMs),
    label: card?.label ?? null,
    away,
    home,
  }
}

/**
 * The plays, newest first, as rows.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * 🔴 THE FEED SENDS OLDEST FIRST AND THE PAGE SHOWS NEWEST FIRST, and the reversal is HERE, once.
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * `plays` is "the last fifty, oldest first (the newest is last)". The design orders them newest
 * first, so what somebody watching the game wants to read is at the top and does not move.
 *
 * ⚠️ A NAME IS ADDED ONLY WHERE THE NBA'S OWN WORDS LEAVE IT OUT. A play's `description` is the
 * NBA's (`B. Adebayo 3PT`, `SUB out: S. Barnes`) and already names the player in the form a
 * fan reads. The box score maps the play's `person_id` to a full name, and that name is shown
 * ahead of the description ONLY when the description does not already contain the player's
 * surname — so `G. Antetokounmpo 14' fadeaway Jump Shot` is not preceded by the same man
 * spelled out. On the recorded feed that is rare by construction (the description names the
 * player in every case but a team event, which has no `person_id`); the mapping is what keeps
 * a description that does NOT name the player (a future feed, a terse action) from being an
 * anonymous line.
 */
export function playRows(game) {
  const plays = Array.isArray(game.plays) ? game.plays : []
  const byId = new Map()
  for (const side of ['home', 'away']) {
    for (const player of game.players?.[side] ?? []) {
      if (Number.isInteger(player.person_id) && typeof player.name === 'string') {
        byId.set(player.person_id, player.name)
      }
    }
  }
  const seen = new Map()
  const rows = plays.map((play) => {
    const description = typeof play.description === 'string' ? play.description : ''
    const mapped = Number.isInteger(play.person_id) ? byId.get(play.person_id) ?? null : null
    const family = mapped === null ? '' : surname(mapped).toLowerCase()
    const named = mapped !== null && family !== '' && !description.toLowerCase().includes(family)
    const side =
      play.team && play.team === game.home?.tricode
        ? 'home'
        : play.team && play.team === game.away?.tricode
          ? 'away'
          : null
    const periodLabel = periodName(play.period)
    const when =
      periodLabel === null
        ? ''
        : typeof play.clock === 'string' && play.clock !== ''
          ? `${periodLabel} ${play.clock}`
          : periodLabel
    const score =
      Number.isInteger(play.score_away) && Number.isInteger(play.score_home)
        ? `${play.score_away}–${play.score_home}`
        : null
    // A key that survives the window sliding: the same play keeps the same key from one poll
    // to the next, so the list is not torn down and rebuilt every fifteen seconds.
    const base = [play.period, play.clock, description, play.score_away, play.score_home].join('|')
    const n = (seen.get(base) ?? 0) + 1
    seen.set(base, n)
    return {
      key: `${base}|${n}`,
      when,
      description,
      name: named ? mapped : null,
      side,
      scoring: play.shot === 'made',
      score,
    }
  })
  return rows.filter((row) => row.description !== '' || row.name !== null).reverse()
}
